import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionArbitrageManagerAxiosApi } from 'src/Config/axiosApi';

export interface PredictionCost {
  _id: string;
  description: string;
  amountUsd: number;
  date: string;
  createdAt: string;
  updatedAt: string;
}

export interface CostsMonthlyEntry {
  month: string;
  totalUsd: number;
  count: number;
}

export interface CostsMonthlyResponse {
  monthly: CostsMonthlyEntry[];
  totalAllTime: number;
}

export interface RealPnlResponse {
  pnlUsd: number;
  totalCostsUsd: number;
  realPnlUsd: number;
  monthly: CostsMonthlyEntry[];
}

const invalidate = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries('prediction-costs');
  qc.invalidateQueries('prediction-costs-monthly');
  qc.invalidateQueries('prediction-real-pnl');
};

export function useCosts() {
  return useQuery(['prediction-costs'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PredictionCost[]>(
        '/positions/costs'
      );
    return data;
  });
}

export function useCostsMonthly() {
  return useQuery(['prediction-costs-monthly'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<CostsMonthlyResponse>(
        '/positions/costs/monthly'
      );
    return data;
  });
}

export function useRealPnl() {
  return useQuery(
    ['prediction-real-pnl'],
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.get<RealPnlResponse>(
          '/positions/real-pnl'
        );
      return data;
    },
    { refetchInterval: 30000 }
  );
}

export function useCreateCost() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { description: string; amountUsd: number; date?: string }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post(
        '/positions/costs',
        body
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}

export function useUpdateCost() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      ...body
    }: {
      id: string;
      description?: string;
      amountUsd?: number;
      date?: string;
    }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.patch(
        `/positions/costs/${id}`,
        body
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}

export function useDeleteCost() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await predictionArbitrageManagerAxiosApi.delete(
        `/positions/costs/${id}`
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}
